import { issueDifficultyValues } from "~/lib/enums"
import { UnassignedIdentity } from "~/components/EntityIdentity"

import { IssueDifficulty, IssuePriority, IssueStatus } from "~/lib/enums"

import type { RouterOutputs } from "~/trpc/react"
import { DifficultyIcon } from "../icons/difficulty"
import { PriorityIcon } from "../icons/priority"
import { StatusIcon } from "../icons/status"
import { EntityIdentity } from "~/components/EntityIdentity"
import { Button } from "../ui/button"
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from "../ui/dialog"

function ValueDisplay({
	value,
	type,
	member
}: {
	value: string
	type: "status" | "priority" | "assignee" | "difficulty"
	member?: RouterOutputs["issues"]["getTeamIssues"]["issues"][number]["assignee"]
}) {
	switch (type) {
		case "status": {
			const status = value as IssueStatus
			if (!Object.values(IssueStatus).includes(status)) {
				return <span>Invalid status: {value}</span>
			}
			return <StatusIcon status={status} withText />
		}
		case "difficulty": {
			const difficulty =
				value === "null"
					? null
					: (Number(value) as NonNullable<IssueDifficulty>)
			if (difficulty !== null && !issueDifficultyValues.includes(difficulty)) {
				return <span>Invalid difficulty: {value}</span>
			}
			return <DifficultyIcon difficulty={difficulty} withText />
		}
		case "priority": {
			const priority = value as IssuePriority
			if (!Object.values(IssuePriority).includes(priority)) {
				return <span>Invalid priority: {value}</span>
			}
			return <PriorityIcon priority={priority} withText />
		}
		case "assignee":
			if (value === "unassigned") {
				return (
					<div className="flex items-center gap-2">
						<UnassignedIdentity size="md" />
						<span>Unassigned</span>
					</div>
				)
			}
			if (!member?.orgMember.user) return null
			return (
				<EntityIdentity
					kind="user"
					userId={member.orgMember.userId}
					name={member.orgMember.user.name}
					image={member.orgMember.user.image}
				/>
			)
	}
}

export function ChangeDialog({
	isOpen,
	onClose,
	fromValue,
	toValue,
	field,
	isLoading,
	onConfirm,
	fromMember,
	toMember
}: {
	isOpen: boolean
	onClose: () => void
	fromValue: string
	toValue: string
	field: "status" | "priority" | "assignee" | "difficulty"
	isLoading: boolean
	onConfirm: () => void
	fromMember?: RouterOutputs["issues"]["getTeamIssues"]["issues"][number]["assignee"]
	toMember?: RouterOutputs["issues"]["getTeamIssues"]["issues"][number]["assignee"]
}) {
	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{field === "status"
							? "Change Status"
							: field === "priority"
								? "Change Priority"
								: field === "difficulty"
									? "Change Difficulty"
									: "Change Assignee"}
					</DialogTitle>
					<div className="flex items-center gap-4">
						<div className="flex-1 space-y-1">
							<div className="text-sm text-text-small">From</div>
							<ValueDisplay
								value={fromValue}
								type={field}
								member={fromMember}
							/>
						</div>
						<div className="flex-1 space-y-1">
							<div className="text-sm text-text-small">To</div>
							<ValueDisplay value={toValue} type={field} member={toMember} />
						</div>
					</div>
					<p className="text-text-main mt-4">
						{field === "status"
							? "Are you sure you want to change the status of this issue?"
							: field === "priority"
								? "Are you sure you want to change the priority of this issue?"
								: field === "difficulty"
									? "Are you sure you want to change the difficulty of this issue?"
									: "Are you sure you want to change who this issue is assigned to?"}
					</p>
				</DialogHeader>
				<DialogFooter>
					<Button variant="ghost" onClick={onClose}>
						Cancel
					</Button>
					<Button onClick={onConfirm} disabled={isLoading}>
						{isLoading ? "Saving..." : "Confirm"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
